import React, { createContext, useContext } from 'react';

import type { ActionVerdict, AssistantAction } from '../types';

// What a transcript needs from the surface around it, and nothing else. Deliberately narrow: the
// assistant's own ConversationContext is bound to its transport (useAssistant - its paths, its
// streaming, its attachments), so a surface plumbed differently could not supply it. These three
// carry no transport, which is what lets the doc editor's Redux + ActionCable panel render the same
// transcript as the assistant sidebar.
//
// Inline tool cards still read ConversationContext. They are only reached through a message's
// inlineToolCalls, so a surface whose agent has no inline tools never mounts one.
export interface TranscriptContextValue {
  busy: boolean;
  showAdminDetails: boolean;
  resolveAction: (action: AssistantAction, verdict: ActionVerdict) => void;
}

const TranscriptContext = createContext<TranscriptContextValue | null>(null);

export function useTranscriptContext() {
  const context = useContext(TranscriptContext);
  if (!context) {
    throw new Error('useTranscriptContext must be used within a TranscriptProvider');
  }

  return context;
}

interface Props {
  children: React.ReactNode;
  value: TranscriptContextValue;
}

export function TranscriptProvider({ children, value }: Props) {
  return <TranscriptContext.Provider value={value}>{children}</TranscriptContext.Provider>;
}
